import { create } from "zustand"

/**
 * Stores pending actions to dispatch to matter-specific AI
 * Used when clicking a matter card in the Global AI drawer
 */
interface PendingAction {
  matterId: string
  query: string
  timestamp: number
}

interface PendingActionState {
  // Current pending action
  pendingAction: PendingAction | null

  // Set a pending action (called when clicking a matter card)
  setPendingAction: (matterId: string, query: string) => void

  // Clear the pending action (called after it's been executed)
  clearPendingAction: () => void

  // Check if there's a pending action for a specific matter
  hasPendingActionFor: (matterId: string) => boolean

  // Consume the pending action for a matter (get and clear)
  consumePendingAction: (matterId: string) => string | null
}

export const usePendingActionStore = create<PendingActionState>()((set, get) => ({
  pendingAction: null,

  setPendingAction: (matterId, query) =>
    set({
      pendingAction: {
        matterId,
        query,
        timestamp: Date.now(),
      },
    }),

  clearPendingAction: () => set({ pendingAction: null }),

  hasPendingActionFor: (matterId) => {
    const { pendingAction } = get()
    if (!pendingAction) return false

    // Check if it's for this matter and not stale (5 minutes max)
    const isForMatter = pendingAction.matterId === matterId
    const isNotStale = Date.now() - pendingAction.timestamp < 5 * 60 * 1000

    return isForMatter && isNotStale
  },

  consumePendingAction: (matterId) => {
    const { pendingAction, clearPendingAction, hasPendingActionFor } = get()

    if (!hasPendingActionFor(matterId)) {
      return null
    }

    const query = pendingAction!.query
    clearPendingAction()
    return query
  },
}))
